import { Dropdown, Input, Skeleton } from 'antd';
import { EditIcon } from 'components/Icons';
import { RevxButton } from 'components/RevxButton';
import RevxFlexLayout from 'components/RevxFlexLayout';
import { RevxMessage } from 'components/RevxMessage';
import { RevxOverlay } from 'components/RevxOverlay';
import { Text, TextHeavy, UnderlineText } from 'components/RevxTypography';
import dayjs from 'dayjs';
import { CarrierTypeEnum, TrackingInfo } from 'domains/shippingDomain';
import { compact } from 'lodash-es';
import { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import shippingService from 'services/shippingService';
import { SHIPPING_STATUS_DISPLAY } from 'utils/constants';
import { toast } from 'components/RevxMessage';
import './index.less';

type Props = {
  carrierType: CarrierTypeEnum;
  value?: string;
  defaultEmptyEditing?: boolean;
  editable?: boolean;
  size?: 'small' | 'default';
  onChange?: (trackingNumber: string) => void;
};
const DATE_TIME_FORMAT = 'MM/DD/YYYY hh:mm A';

const getOverlay = (trackingInfo: TrackingInfo | null) => {
  if (trackingInfo && trackingInfo.length > 0) {
    const lastItem = trackingInfo[0];
    return (
      <>
        <div>
          <TextHeavy style={{ marginRight: 8 }}>Last Updated:</TextHeavy>
          {lastItem && dayjs(lastItem.dateTime).format(DATE_TIME_FORMAT)}
        </div>
        <div className="revx-tracking-info-detail">
          {trackingInfo.map((detailItem) => {
            return (
              <div
                className="revx-tracking-info-detail-item"
                key={detailItem.id}
              >
                <RevxFlexLayout>
                  <Text className="revx-tracking-info-detail-time">
                    {dayjs(detailItem.dateTime).format(DATE_TIME_FORMAT)}
                  </Text>
                  <Text>
                    <TextHeavy>
                      {detailItem.description ||
                        SHIPPING_STATUS_DISPLAY[detailItem.status]}
                    </TextHeavy>
                    <div>
                      {compact([
                        detailItem.city,
                        detailItem.stateProvince,
                        detailItem.country,
                      ]).join(', ')}
                    </div>
                  </Text>
                </RevxFlexLayout>
              </div>
            );
          })}
        </div>
      </>
    );
  } else {
    return null;
  }
};

export const RevxTrackingInfo = ({
  carrierType,
  value,
  onChange,
  editable,
  size = 'small',
  defaultEmptyEditing,
}: Props) => {
  const [trackingInfo, setTrackingInfo] =
    useState<TrackingInfo | string | null>(null);
  const [loading, setLoading] = useState<boolean | null>(null);
  const [editing, setEditing] = useState(defaultEmptyEditing ? !value : false);
  const [visible, setVisible] = useState(false);
  const [needLoadTrackingInfo, setNeedLoadTrackingInfo] = useState(false);
  const [curValue, setCurValue] = useState(value);
  const { t } = useTranslation();

  useEffect(() => {
    setCurValue(value);
  }, [value]);

  useEffect(() => {
    if (value) {
      setNeedLoadTrackingInfo(true);
    }
  }, [value]);

  useEffect(() => {
    if (defaultEmptyEditing) {
      if (!value) {
        setEditing(true);
      } else {
        setEditing(false);
      }
    }
  }, [defaultEmptyEditing, value]);

  return (
    <Dropdown
      trigger={['click', 'hover']}
      placement="bottomRight"
      arrow
      visible={visible}
      onVisibleChange={async (visible) => {
        if (visible && (editing || !value)) {
          setVisible(false);
          return;
        }
        setVisible(visible);
        if (visible && !loading && value && needLoadTrackingInfo) {
          try {
            setLoading(true);
            const trackingInfo = await shippingService.getTrackingInfo(
              carrierType,
              value
            );
            setTrackingInfo(trackingInfo);
          } catch (error) {
            setTrackingInfo((error as ErrorEvent).message);
          } finally {
            setLoading(false);
            setNeedLoadTrackingInfo(false);
          }
        }
      }}
      overlay={
        <RevxOverlay
          className="revx-tracking-info"
          style={{ minHeight: loading ? 494 : 'unset' }}
        >
          {loading ? (
            <Skeleton />
          ) : typeof trackingInfo === 'string' ? (
            <RevxMessage message={trackingInfo} type="error" showIcon />
          ) : (
            getOverlay(trackingInfo)
          )}
        </RevxOverlay>
      }
    >
      <span>
        {editing ? (
          <>
            <RevxFlexLayout>
              <Input
                placeholder="Enter tracking ID"
                className="revx-tracking-info-input"
                onChange={(e) => {
                  setCurValue(e.target.value);
                }}
                value={curValue}
              />
              <RevxButton
                type="primary"
                className="revx-tracking-info-button"
                onClick={async (e) => {
                  if (!curValue) {
                    toast.error(
                      t(
                        'workspace.detail.supplierShipping.trackingNumberErrorMsg'
                      )
                    );
                    return;
                  }
                  e.stopPropagation();
                  if (onChange) {
                    onChange(curValue + '');
                  }
                  setEditing(false);
                }}
              >
                {t('common.submit')}
              </RevxButton>
            </RevxFlexLayout>
          </>
        ) : (
          <>
            <UnderlineText
              size="small"
              color="blue"
              className="workspace-item-content-row-t"
            >
              {value}
            </UnderlineText>
            <span
              hidden={!editable}
              style={{ color: '#757575', paddingLeft: 4, cursor: 'pointer' }}
              onClick={(e) => {
                e.stopPropagation();
                if (!editing) {
                  setEditing(true);
                }
              }}
            >
              <EditIcon />
            </span>
          </>
        )}
      </span>
    </Dropdown>
  );
};
